"use client";
import React from "react";
import Table from "@/components/primitives/table";
import { ColumnDef } from "@tanstack/react-table";
import {
  GameAlignmentType,
  HeadToHeadRecentGame,
  LineType,
  OddOutcome,
  RecentGameNew,
  RecentGamesNew,
  TeamBasic,
  buildOdd,
} from "common";
import {
  formatDate,
  // formatIsoDate,
  formatOdd,
} from "@/utils";
import TeamLogo from "@/components/team/logo";
import DoubleLogo from "@/components/team/double-logo";
import Link from "next/link";
import { createGameDetailSlug } from "@/utils/url";
import GameTableScore from "../table/score";
import GameTableOddOutcome from "../table/odd-outcome";
import { useOddsFormat } from "@/utils/providers/odds-format";

export default function RecentGamesTable({
  data,
  view,
}: {
  data: RecentGamesNew;
  view: string;
}) {
  const { oddsFormat } = useOddsFormat();

  const [processedData, setProcessedData] = React.useState(data);

  React.useEffect(() => {
    const processed: RecentGamesNew = {
      ...data,
      headToHeadlast10Games: data.headToHeadlast10Games.map((row) => ({
        ...row,
        dateTime: formatDate(row.dateTime, true, true, true),
      })),
      awayLast10Games: data.awayLast10Games.map((row) => ({
        ...row,
        dateTime: formatDate(row.dateTime, true),
      })),
      homeLast10Games: data.homeLast10Games.map((row) => ({
        ...row,
        dateTime: formatDate(row.dateTime, true),
      })),
    };
    setProcessedData(processed);
  }, [data]);

  const h2hColumns: ColumnDef<HeadToHeadRecentGame>[] = React.useMemo(
    () => [
      {
        header: "date",
        accessorKey: "dateTime",
        size: 110,
      },
      {
        header: "result",
        size: 110,
        cell: (info) => {
          const row = info.row.original;
          return (
            <>
              <Link
                href={createGameDetailSlug(row.league, row.odds?.gameId || 0)}
                className="flex items-center gap-2"
              >
                {row.outcome === "away" ? (
                  <TeamLogo team={row.awayTeam} size="xs" />
                ) : row.outcome === "home" ? (
                  <TeamLogo team={row.homeTeam} size="xs" />
                ) : (
                  <DoubleLogo
                    awayTeam={row.awayTeam}
                    homeTeam={row.homeTeam}
                    size="xs"
                  />
                )}
                <div>
                  {row.outcome === "away" ? row.awayScore : row.homeScore} -{" "}
                  {row.outcome === "away" ? row.homeScore : row.awayScore}
                </div>
              </Link>
            </>
          );
        },
      },
      {
        id: "home",
        header: "home",
        size: 96,
        cell: (info) => {
          return (
            <div className="flex items-center gap-2 text-title-sm-medium">
              <div className="text-title-lg-medium uppercase text-dark">
                {info.row.original.homeTeam.abbreviation}
              </div>
            </div>
          );
        },
      },
      {
        header: "moneyline",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          let winningAlignment: GameAlignmentType = GameAlignmentType.Home;
          let winningTeam: TeamBasic | null = null;
          switch (row.odds?.awayMoneylineOutcome) {
            case OddOutcome.Push:
              winningAlignment = GameAlignmentType.Home;
              winningTeam = null;
              break;
            case OddOutcome.Win:
              winningAlignment = GameAlignmentType.Away;
              winningTeam = row.awayTeam;
              break;
            case OddOutcome.Loss:
              winningAlignment = GameAlignmentType.Home;
              winningTeam = row.homeTeam;
              break;
          }
          const odd =
            row.outcome !== "tie"
              ? buildOdd(LineType.Moneyline, winningAlignment, row.odds)
              : undefined;
          return (
            <div className="flex items-center gap-2 text-title-sm-medium">
              <GameTableOddOutcome odd={odd} winningTeam={winningTeam} />
            </div>
          );
        },
      },
      {
        header: "spread",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          let winningAlignment: GameAlignmentType = GameAlignmentType.Home;
          let winningTeam: TeamBasic | null = null;
          switch (row.odds?.awaySpreadOutcome) {
            case OddOutcome.Push:
              winningAlignment = GameAlignmentType.Home;
              winningTeam = null;
              break;
            case OddOutcome.Win:
              winningAlignment = GameAlignmentType.Away;
              winningTeam = row.awayTeam;
              break;
            case OddOutcome.Loss:
              winningAlignment = GameAlignmentType.Home;
              winningTeam = row.homeTeam;
              break;
          }
          const odd =
            row.outcome !== "tie"
              ? buildOdd(LineType.Spread, winningAlignment, row.odds)
              : undefined;
          return (
            <div className="flex items-center gap-2 text-title-sm-medium">
              <GameTableOddOutcome odd={odd} winningTeam={winningTeam} />
            </div>
          );
        },
      },

      {
        header: "total (o/u)",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          let winningAlignment: GameAlignmentType = GameAlignmentType.Home;
          let winningTeam: TeamBasic | null = null;
          switch (row.odds?.overOutcome) {
            case OddOutcome.Push:
              winningAlignment = GameAlignmentType.Home;
              winningTeam = null;
              break;
            case OddOutcome.Win:
              winningAlignment = GameAlignmentType.Away;
              winningTeam = row.awayTeam;
              break;
            case OddOutcome.Loss:
              winningAlignment = GameAlignmentType.Home;
              winningTeam = row.homeTeam;
              break;
          }
          const odd =
            row.outcome !== "tie"
              ? buildOdd(LineType.Total, winningAlignment, row.odds)
              : undefined;
          return (
            <div className="flex items-center gap-2 text-title-sm-medium">
              <span>{formatOdd(oddsFormat, odd, true)?.value}</span>
            </div>
          );
        },
      },
    ],
    []
  );
  const columns: ColumnDef<RecentGameNew>[] = React.useMemo(
    () => [
      {
        header: "date",
        size: 120,
        accessorKey: "dateTime",
      },
      {
        header: "opponent",
        size: 113,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Link
              href={createGameDetailSlug(row.league, row.odds?.gameId || 0)}
              className="flex items-center gap-2"
            >
              <div className="text-title-lg text-secondary-300 w-5">
                {row.alignment === "home" ? "vs." : "@"}
              </div>
              <TeamLogo team={row.opponent} size="xs" />
              <div className="text-title-lg-medium uppercase text-dark w-7">
                {row.opponent.abbreviation}
              </div>
            </Link>
          );
        },
      },
      {
        header: "result",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Link
              href={createGameDetailSlug(row.league, row.odds?.gameId || 0)}
              className="flex items-center gap-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-title-sm-bold">
                  {row.outcome === "tie" ? (
                    <span className="text-orange">T</span>
                  ) : row.outcome === row.alignment ? (
                    <span className="text-success-300">W</span>
                  ) : (
                    <span className="text-error-300">L</span>
                  )}
                </span>
                <span>
                  {row.alignment === "away" ? row.awayScore : row.homeScore} -{" "}
                  {row.alignment === "away" ? row.homeScore : row.awayScore}
                </span>
              </div>
            </Link>
          );
        },
      },
      {
        header: "moneyline",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          const odd = buildOdd(
            LineType.Moneyline,
            row.alignment === "away"
              ? GameAlignmentType.Away
              : GameAlignmentType.Home,
            row.odds
          );
          return <GameTableOddOutcome odd={odd} />;
        },
      },
      {
        header: "spread",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          const odd = buildOdd(
            LineType.Spread,
            row.alignment === "away"
              ? GameAlignmentType.Away
              : GameAlignmentType.Home,
            row.odds
          );

          return <GameTableOddOutcome odd={odd} />;
        },
      },

      {
        header: "total (o/u)",
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          const odd =
            row.outcome !== "tie"
              ? buildOdd(
                  LineType.Total,
                  row.odds?.overOutcome === OddOutcome.Win
                    ? GameAlignmentType.Away
                    : GameAlignmentType.Home,
                  row.odds
                )
              : undefined;
          return (
            <div className="flex items-center gap-2 text-title-sm-medium">
              <span>{formatOdd(oddsFormat, odd)?.value}</span>
            </div>
          );
        },
      },
    ],
    []
  );
  if (view === "h2h") {
    return (
      <Table
        type="banded"
        columns={h2hColumns}
        data={processedData.headToHeadlast10Games}
      />
    );
  } else {
    return (
      <Table
        type="banded"
        columns={columns}
        data={
          view === "away"
            ? processedData.awayLast10Games
            : processedData.homeLast10Games
        }
      />
    );
  }
}
